import { EditorContent } from '@tiptap/react';
import React, { MutableRefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { clamp } from 'lodash';

import { ImMNElement } from '../../../../common/elements/elementModelTypes';
import { TABLE_CELL_EDITOR_KEY } from '../../../../common/table/tableConstants';
import type { CellData, CellEditingSession } from '../../../../common/table/TableTypes';
import type { TiptapContent } from '../../../../common/tiptap/tiptapTypes';
import getTiptapNodeTextContent from '../../../../common/tiptap/utils/jsonContentUtils/getTiptapNodeTextContent';
import { MilanoteEditorRef } from '../../../components/editor/milanoteEditor/MilanoteEditorTypes';
import useClientElementTiptapEditor from '../../../components/tiptapEditor/useClientElementTiptapEditor';
import useUpdatedRef from '../../../utils/react/useUpdatedRef';
import { MNHotInstance } from '../tableHotInstanceTypes';
import { getTableCellEditorId } from '../utils/tableEditorSessionUtils';
import { getTiptapCellEditorContent, getTiptapContentFromPlainText } from './tiptapCellEditorContentUtils';
import useTableCellTextAlignment from './useTableCellTextAlignment';
import { useTableCellEditorTiptapExtensions } from './useTableCellTiptapExtensions';

type Props = {
    element: ImMNElement;
    elementId: string;
    isEditable: boolean;
    cellData: CellData | undefined;
    cellEditingSession: CellEditingSession | null;
    currentlyEditingEditorId: string | null;
    hotTableInstanceRef: MutableRefObject<MNHotInstance<TiptapContent, TiptapContent> | null>;
    cellEditorRef: MutableRefObject<MilanoteEditorRef<TiptapContent> | null>;
    locale: string;
};

const TiptapCellEditorComponent: React.FC<Props> = ({
    elementId,
    isEditable,
    cellData,
    cellEditingSession,
    currentlyEditingEditorId,
    hotTableInstanceRef,
    locale,
    cellEditorRef,
}) => {
    const { textAlignment, recomputeEditingTextAlignment } = useTableCellTextAlignment({
        cellData,
        cellEditingSession,
        locale,
    });

    const tiptapEditorRef = useRef<HTMLDivElement | null>(null);
    const cellEditingSessionRef = useUpdatedRef(cellEditingSession);
    const recomputeEditingTextAlignmentRef = useUpdatedRef(recomputeEditingTextAlignment);

    const saveContent = useCallback(
        (content: TiptapContent) => {
            if (!cellEditingSessionRef.current) return;

            recomputeEditingTextAlignmentRef.current(getTiptapNodeTextContent(content));

            const activeEditor = hotTableInstanceRef.current?.getMNEditor();
            activeEditor?.onMilanoteCellEditorChange(content);

            // @ts-ignore - Custom Milanote hook
            hotTableInstanceRef.current?.runHooks('onMilanoteCellEditorChange', content);
        },
        [hotTableInstanceRef],
    );

    const onCommitEdit = useCallback(() => {
        const hotInstance = hotTableInstanceRef.current;
        const session = cellEditingSessionRef.current;
        if (!hotInstance || !session) return;

        const activeEditor = hotInstance.getActiveEditor();
        if (!activeEditor) return;

        const { row, col } = session.cellCoords;
        const nextRow = clamp(row + 1, 0, hotInstance.countRows() - 1);

        // Because we suppress the default enter behaviour (in TableCellCommitEditTiptapExtension),
        // we need to manually finish editing and move the selection to the next cell.
        activeEditor.finishEditing(false, false, () => {
            hotInstance.selectCell(nextRow, col);
        });
    }, [cellEditingSessionRef, hotTableInstanceRef]);

    const extensions = useTableCellEditorTiptapExtensions({ isEditable, saveContent, onCommitEdit });
    const editorId = getTableCellEditorId(elementId);

    const tiptapContent = useMemo(
        () => getTiptapCellEditorContent({ cellData, cellEditingSession, locale }),
        [cellData, cellEditingSession, locale],
    );

    const { editor } = useClientElementTiptapEditor({
        persistedContent: tiptapContent,
        extensions,

        editorId,
        editorKey: TABLE_CELL_EDITOR_KEY,
        currentEditorId: currentlyEditingEditorId || undefined,

        isEditable,
        isEditing: !!cellEditingSession,
        isSingleSelected: true,
    });

    useLayoutEffect(() => {
        if (!cellEditingSession || !editor || !tiptapContent) return;

        editor.commands.syncFromExternalStore(tiptapContent);
        editor.commands.focus('end');

        // Keep plugin hooks in sync with the initial editor content when edit mode opens.
        // (eg formula reference highlighting would otherwise wait for the first edit rather than showing immediately)
        // @ts-ignore - Custom Milanote hook
        hotTableInstanceRef.current?.runHooks('onMilanoteCellEditorChange', tiptapContent);
    }, [cellEditingSession, editor, tiptapContent]);

    useEffect(() => {
        // TODO-TABLES: This can be refactored to be less hacky after draft is removed
        const editorWrapper: MilanoteEditorRef<TiptapContent> = {
            getEditor: () => editor,
            getEditorState: () => (editor ? (editor.getJSON() as any) : null),
            onChange: (content) => editor?.commands.setContent(content as any),
            setPlainText: (text: string, inlineStyles?: string[]) =>
                editor?.commands.setContent(getTiptapContentFromPlainText(text, inlineStyles)),
        };
        cellEditorRef.current = editorWrapper;

        return () => {
            if (cellEditorRef.current === editorWrapper) cellEditorRef.current = null;
        };
    }, [cellEditorRef, editor]);

    useEffect(() => {
        // This lets handsontable know which element is the active editor
        tiptapEditorRef.current!.setAttribute('data-hot-input', 'true');
    }, []);

    // Note that this component is initialised once *per table*, and is reused as the edit focus moves around
    // (ie a useEffect with an empty dependency array will run once on table init, not on every edit start)
    return (
        <div className="handsontable-input TableTiptapCellEditor" style={{ textAlign: textAlignment || undefined }}>
            <EditorContent
                innerRef={tiptapEditorRef}
                className="TableTiptapCellRenderer-content"
                dir="auto"
                editor={editor}
            />
        </div>
    );
};

export default TiptapCellEditorComponent;
